import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, Wrench, AlertTriangle, CheckCircle2, Clock, CalendarDays, List, ChevronLeft, ChevronRight, Bookmark } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { useTableExport } from '@/hooks/useTableExport';
import { useSavedFilters } from '@/hooks/useSavedFilters';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, isBefore, isAfter } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ServiceHistoryItem {
  id: string;
  serviceType: string;
  date: string;
  technician: string;
  notes?: string;
  certificateRef?: string;
}

interface Equipment {
  id: string;
  equipmentName: string;
  equipmentCode: string;
  lab: string;
  type: string;
  lastCalibration?: string;
  nextCalibrationDue?: string;
  lastPM?: string;
  nextPMDue?: string;
  status: string;
  responsibleTechnician: string;
  serviceHistory?: ServiceHistoryItem[];
  certificates?: { id: string; name: string; issuedDate: string; expiryDate: string }[];
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const TODAY = new Date();

const MOCK_EQUIPMENT: Equipment[] = [
  {
    id: 'eq1', equipmentCode: 'EQ-00001', equipmentName: 'Agilent 1260 Infinity II HPLC',
    lab: 'QC Central Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-10-15', nextCalibrationDue: '2026-06-17',
    lastPM: '2026-01-10', nextPMDue: '2026-07-17',
    responsibleTechnician: 'Dr. Ramesh Nair',
    serviceHistory: [
      { id:'sh-eq1-3', serviceType:'inspection',  date:'2026-03-05', technician:'Dr. Ramesh Nair',       notes:'Routine performance verification — USP system suitability (tailing <1.5, N>2000 plates). PASS.' },
      { id:'sh-eq1-2', serviceType:'pm',          date:'2026-01-10', technician:'Dr. Ramesh Nair',       notes:'Quarterly PM: pump seals replaced, autosampler needle cleaned, mobile phase lines flushed. Column oven verified ±0.3°C.' },
      { id:'sh-eq1-1', serviceType:'calibration', date:'2025-10-15', technician:'Vendor — Agilent India', notes:'Annual IQ/OQ/PQ per SOP-CAL-HPLC-001. Wavelength accuracy, linearity, pump precision — all pass. UV lamp: 1,840 h.', certificateRef:'CERT-AGL-HPLC-2025-1015' },
    ],
    certificates: [
      { id:'c-eq1-1', name:'Agilent 1260 HPLC IQ/OQ/PQ Certificate 2025–26', issuedDate:'2025-10-15', expiryDate:'2026-10-14' },
    ],
  },
  {
    id: 'eq2', equipmentCode: 'EQ-00002', equipmentName: 'Agilent 8890 GC-FID/MS',
    lab: 'API Synthesis Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-09-01', nextCalibrationDue: '2026-08-05',
    lastPM: '2026-02-10', nextPMDue: '2026-05-02',
    responsibleTechnician: 'Dr. Suresh Kapoor',
    serviceHistory: [
      { id:'sh-eq2-2', serviceType:'pm',          date:'2026-02-10', technician:'Dr. Suresh Kapoor',     notes:'Septum and inlet liner replaced, FID jet cleaned. Carrier gas (He) leak check — PASS. Baseline noise <0.8 pA.' },
      { id:'sh-eq2-1', serviceType:'calibration', date:'2025-09-01', technician:'Vendor — Agilent India', notes:'Annual GC column oven, FID linearity, inlet temp. calibration. NABL certificate issued. Residual solvents (ICH Q3C) scope confirmed.', certificateRef:'CERT-AGL-GC-2025-0901' },
    ],
    certificates: [
      { id:'c-eq2-1', name:'GC-FID/MS Annual NABL Calibration Certificate 2025–26', issuedDate:'2025-09-01', expiryDate:'2026-08-31' },
    ],
  },
  {
    id: 'eq3', equipmentCode: 'EQ-00003', equipmentName: 'Distek 2500 Dissolution System',
    lab: 'QC Central Laboratory', type: 'testing', status: 'due-soon',
    lastCalibration: '2025-10-20', nextCalibrationDue: '2026-04-25',
    lastPM: '2025-10-20', nextPMDue: '2026-06-20',
    responsibleTechnician: 'Kavita Lal',
    serviceHistory: [
      { id:'sh-eq3-2', serviceType:'repair',      date:'2026-02-14', technician:'Vendor — Distek India', notes:'Vessel 4 drive belt replaced after rpm deviation >±5%. Post-repair verification PASS.' },
      { id:'sh-eq3-1', serviceType:'calibration', date:'2025-10-20', technician:'Kavita Lal',           notes:'USP <711>: paddle speed 50 rpm ±4% and 100 rpm ±4%. Temperature mapping 37.0±0.5°C across all 8 vessels.', certificateRef:'CERT-DST-2025-1020' },
    ],
    certificates: [
      { id:'c-eq3-1', name:'Distek 2500 Dissolution USP <711> Calibration 2025–26', issuedDate:'2025-10-20', expiryDate:'2026-04-25' },
    ],
  },
  {
    id: 'eq4', equipmentCode: 'EQ-00004', equipmentName: 'Mettler Toledo XPR 204 Analytical Balance',
    lab: 'API Synthesis Laboratory', type: 'weighing', status: 'current',
    lastCalibration: '2025-11-05', nextCalibrationDue: '2026-07-10',
    lastPM: '2026-01-22', nextPMDue: '2026-08-08',
    responsibleTechnician: 'Harish Reddy',
    serviceHistory: [
      { id:'sh-eq4-2', serviceType:'pm',          date:'2026-01-22', technician:'Harish Reddy',          notes:'Draft shield cleaned, levelling checked, internal calibration mass verified. Linearity 1 mg–220 g — PASS.' },
      { id:'sh-eq4-1', serviceType:'calibration', date:'2025-11-05', technician:'NABL Calibration Lab', notes:'External calibration against NABL-traceable E2 reference weights. Readability 0.1 mg, repeatability SD <0.10 mg.', certificateRef:'NABL-CAL-2025-1105' },
    ],
    certificates: [
      { id:'c-eq4-1', name:'Analytical Balance NABL Calibration Certificate 2025–26', issuedDate:'2025-11-05', expiryDate:'2026-05-04' },
    ],
  },
  {
    id: 'eq5', equipmentCode: 'EQ-00005', equipmentName: 'Mettler Toledo V30 Volumetric Karl Fischer Titrator',
    lab: 'QC Central Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-11-18', nextCalibrationDue: '2026-06-06',
    lastPM: '2026-02-28', nextPMDue: '2026-06-28',
    responsibleTechnician: 'Priya Nair',
    serviceHistory: [
      { id:'sh-eq5-2', serviceType:'pm',          date:'2026-02-28', technician:'Priya Nair',             notes:'Reagent lines flushed, Pt electrode polished, titration cell cleaned. Blank titration <0.05 mg H₂O. PASS.' },
      { id:'sh-eq5-1', serviceType:'calibration', date:'2025-11-18', technician:'Vendor — Mettler Toledo', notes:'Volumetric KF calibration with DST water standard. Factor: 4.92 mg H₂O/mL, RSD 0.38%. USP <921> compliant.', certificateRef:'CERT-MT-KF-2025-1118' },
    ],
    certificates: [
      { id:'c-eq5-1', name:'Karl Fischer Titrator Calibration Certificate 2025–26', issuedDate:'2025-11-18', expiryDate:'2026-05-17' },
    ],
  },
  {
    id: 'eq6', equipmentCode: 'EQ-00006', equipmentName: 'Memmert HPP 750 eco Stability Chamber',
    lab: 'Stability Testing Laboratory', type: 'environmental', status: 'due-soon',
    lastCalibration: '2025-10-01', nextCalibrationDue: '2026-05-29',
    lastPM: '2026-01-15', nextPMDue: '2026-07-17',
    responsibleTechnician: 'Dr. Ramesh Nair',
    serviceHistory: [
      { id:'sh-eq6-3', serviceType:'inspection',  date:'2026-03-20', technician:'Dr. Ramesh Nair',       notes:'Quarterly IQOQ check. Temperature excursion log reviewed — 1 event (CAPA-2024-001) under remediation. Chamber performing within spec.' },
      { id:'sh-eq6-2', serviceType:'pm',          date:'2026-01-15', technician:'Vendor — Memmert India', notes:'Compressor service, door gasket replaced, humidity sensor recalibrated, IoT data logger re-synced.' },
      { id:'sh-eq6-1', serviceType:'calibration', date:'2025-10-01', technician:'Bureau Veritas India',   notes:'ICH Q1A Zone IVb (30°C/75%RH) 12-point temperature mapping: ±0.8°C, ±2.2%RH. PASS.', certificateRef:'BV-STAB-CAL-2025-1001' },
    ],
    certificates: [
      { id:'c-eq6-1', name:'ICH Q1A Stability Chamber Mapping Certificate 2025–26', issuedDate:'2025-10-01', expiryDate:'2026-09-30' },
    ],
  },
  {
    id: 'eq7', equipmentCode: 'EQ-00007', equipmentName: 'Agilent 5800 ICP-OES',
    lab: 'API Synthesis Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-07-10', nextCalibrationDue: '2026-09-19',
    lastPM: '2026-01-30', nextPMDue: '2026-10-25',
    responsibleTechnician: 'Dr. Suresh Kapoor',
    serviceHistory: [
      { id:'sh-eq7-2', serviceType:'pm',          date:'2026-01-30', technician:'Dr. Suresh Kapoor',      notes:'Torch cleaned, nebuliser pump tubing replaced, peristaltic pump pressure checked. Plasma alignment verified.' },
      { id:'sh-eq7-1', serviceType:'calibration', date:'2025-07-10', technician:'Vendor — Agilent India', notes:'Multi-element ICH Q3D calibration. Wavelength accuracy ±0.005 nm. 15-element std (Pd, Ni, Pb, Cu, As, Ru…) — PASS.', certificateRef:'CERT-AGL-ICP-2025-0710' },
    ],
    certificates: [
      { id:'c-eq7-1', name:'ICP-OES ICH Q3D Calibration Certificate 2025–26', issuedDate:'2025-07-10', expiryDate:'2026-07-09' },
    ],
  },
  {
    id: 'eq8', equipmentCode: 'EQ-00008', equipmentName: 'Shimadzu UV-2600i UV-Vis Spectrophotometer',
    lab: 'Sterile Injectable QC Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-11-25', nextCalibrationDue: '2026-07-23',
    lastPM: '2026-02-18', nextPMDue: '2026-09-02',
    responsibleTechnician: 'Sneha Patel',
    serviceHistory: [
      { id:'sh-eq8-2', serviceType:'pm',          date:'2026-02-18', technician:'Sneha Patel',             notes:'Deuterium lamp: 2,140 h (within 3,000 h life). Cell compartment cleaned. Baseline flatness <0.002 Abs.' },
      { id:'sh-eq8-1', serviceType:'calibration', date:'2025-11-25', technician:'Vendor — Shimadzu India', notes:'Wavelength accuracy (D₂ & W lamp), photometric accuracy per USP <857>. Stray light <0.05%T. PASS.', certificateRef:'CERT-SHI-UV-2025-1125' },
    ],
    certificates: [
      { id:'c-eq8-1', name:'UV-2600i Spectrophotometer USP <857> Certificate 2025–26', issuedDate:'2025-11-25', expiryDate:'2026-05-24' },
    ],
  },
  {
    id: 'eq9', equipmentCode: 'EQ-00009', equipmentName: 'Charles River Endosafe PTS (LAL BET Reader)',
    lab: 'Microbiology Laboratory', type: 'microbiological', status: 'current',
    lastCalibration: '2025-10-12', nextCalibrationDue: '2026-10-12',
    lastPM: '2026-03-01', nextPMDue: '2026-09-30',
    responsibleTechnician: 'Kavita Lal',
    serviceHistory: [
      { id:'sh-eq9-2', serviceType:'inspection',  date:'2026-03-01', technician:'Kavita Lal',             notes:'Cartridge lot verification (LPS-E30 lot 15042). QC standard: all 4 readings within 50–200% recovery. PASS.' },
      { id:'sh-eq9-1', serviceType:'calibration', date:'2025-10-12', technician:'Charles River India',    notes:'Annual PTS calibration: CSE std curve linearity r²>0.998, CV<10%. USP <85>/Ph.Eur.2.6.14 compliant.', certificateRef:'CR-PTS-CAL-2025-1012' },
    ],
    certificates: [
      { id:'c-eq9-1', name:'Endosafe PTS Annual Calibration Certificate 2025–26', issuedDate:'2025-10-12', expiryDate:'2026-10-11' },
    ],
  },
  {
    id: 'eq10', equipmentCode: 'EQ-00010', equipmentName: 'Tuttnauer EZ11 Benchtop Autoclave',
    lab: 'Microbiology Laboratory', type: 'sterilization', status: 'due-soon',
    lastCalibration: '2025-09-20', nextCalibrationDue: '2026-04-25',
    lastPM: '2026-01-08', nextPMDue: '2026-07-08',
    responsibleTechnician: 'Kavita Lal',
    serviceHistory: [
      { id:'sh-eq10-3', serviceType:'repair',      date:'2026-03-10', technician:'Vendor — Tuttnauer India', notes:'Steam solenoid valve replaced after incomplete cycle alarm. Post-repair validation cycle — PASS.' },
      { id:'sh-eq10-2', serviceType:'pm',          date:'2026-01-08', technician:'Kavita Lal',               notes:'Door gasket replaced, valve seats cleaned, safety valve tested. BI (G. stearothermophilus) run — negative at 15 min cycle.' },
      { id:'sh-eq10-1', serviceType:'calibration', date:'2025-09-20', technician:'Vendor — Tuttnauer India', notes:'Annual thermocouple calibration and Bowie-Dick test. Temp. uniformity 121°C ±0.8°C at 15 PSI. SAL maintained.', certificateRef:'CERT-TUT-EZ11-2025-0920' },
    ],
    certificates: [
      { id:'c-eq10-1', name:'Autoclave Calibration & SAL Validation Certificate 2025–26', issuedDate:'2025-09-20', expiryDate:'2026-09-19' },
    ],
  },
  {
    id: 'eq11', equipmentCode: 'EQ-00011', equipmentName: 'SKAN RABS Sterility Testing Isolator',
    lab: 'Sterile Injectable QC Laboratory', type: 'sterilization', status: 'current',
    lastCalibration: '2025-10-28', nextCalibrationDue: '2026-10-28',
    lastPM: '2026-02-05', nextPMDue: '2026-07-30',
    responsibleTechnician: 'Sneha Patel',
    serviceHistory: [
      { id:'sh-eq11-2', serviceType:'inspection',  date:'2026-02-05', technician:'Sneha Patel',           notes:'Quarterly EM inside isolator: Grade A / ISO 5 confirmed. Particle count <1 CFU/4 h per USP <71>. PASS.' },
      { id:'sh-eq11-1', serviceType:'calibration', date:'2025-10-28', technician:'Vendor — SKAN AG India', notes:'Annual RABS validation: HEPA DOP test, airflow ≥0.45 m/s, positive pressure leak test, glove integrity. WHO TRS 1010 PASS.', certificateRef:'CERT-SKAN-RABS-2025-1028' },
    ],
    certificates: [
      { id:'c-eq11-1', name:'RABS Isolator Annual Validation Certificate (WHO TRS 1010)', issuedDate:'2025-10-28', expiryDate:'2026-10-27' },
    ],
  },
  {
    id: 'eq12', equipmentCode: 'EQ-00012', equipmentName: 'Malvern Mastersizer 3000 Particle Size Analyzer',
    lab: 'API Synthesis Laboratory', type: 'analytical', status: 'current',
    lastCalibration: '2025-11-10', nextCalibrationDue: '2026-09-02',
    lastPM: '2026-02-22', nextPMDue: '2026-07-28',
    responsibleTechnician: 'Harish Reddy',
    serviceHistory: [
      { id:'sh-eq12-2', serviceType:'pm',          date:'2026-02-22', technician:'Harish Reddy',               notes:'Laser head cleaned, flow cell replaced, pump pressure checked. Background scatter within instrument specification.' },
      { id:'sh-eq12-1', serviceType:'calibration', date:'2025-11-10', technician:'Vendor — Malvern Panalytical', notes:'Annual check with NIST SRM 1003c (18.1 µm polystyrene). D50: 18.05±0.25 µm. USP <429> compliant. PASS.', certificateRef:'CERT-MAL-MS3-2025-1110' },
    ],
    certificates: [
      { id:'c-eq12-1', name:'Mastersizer 3000 NIST Calibration Certificate 2025–26', issuedDate:'2025-11-10', expiryDate:'2026-05-09' },
    ],
  },
];

const STATUS_OPTIONS = ['current', 'due-soon', 'overdue', 'in-service'];
const TYPE_OPTIONS = ['analytical', 'weighing', 'sterilization', 'testing', 'environmental', 'microbiological'];
const LABS = ['QC Central Laboratory', 'API Synthesis Laboratory', 'Stability Testing Laboratory', 'Microbiology Laboratory', 'Sterile Injectable QC Laboratory'];
const SERVICE_TYPES = ['calibration', 'pm', 'repair', 'inspection'];

const STATUS_DOT_COLOR: Record<string, string> = {
  current: '#16A34A',
  'due-soon': '#D97706',
  overdue: '#DC2626',
  'in-service': '#2563EB',
};

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Calendar View ─────────────────────────────────────────────────────────────

function CalendarView({ equipment }: { equipment: Equipment[] }) {
  const [calMonth, setCalMonth] = useState(new Date());

  const monthStart = startOfMonth(calMonth);
  const monthEnd = endOfMonth(calMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Build a map of date string → array of {name, status}
  const dueDateMap: Record<string, { name: string; status: string }[]> = {};
  equipment.forEach((eq) => {
    const addEntry = (dateStr: string | undefined, status: string) => {
      if (!dateStr) return;
      const d = format(new Date(dateStr), 'yyyy-MM-dd');
      if (!dueDateMap[d]) dueDateMap[d] = [];
      dueDateMap[d].push({ name: eq.equipmentCode, status });
    };
    addEntry(eq.nextCalibrationDue, eq.status);
    addEntry(eq.nextPMDue, eq.status);
  });

  const startDow = getDay(monthStart); // 0=Sun

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif font-bold text-base text-text">{format(calMonth, 'MMMM yyyy')}</h3>
        <div className="flex gap-1">
          <button onClick={() => setCalMonth(subMonths(calMonth, 1))} className="btn-ghost p-1.5"><ChevronLeft size={14} /></button>
          <button onClick={() => setCalMonth(addMonths(calMonth, 1))} className="btn-ghost p-1.5"><ChevronRight size={14} /></button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-text-muted">
        {['overdue', 'due-soon', 'current'].map((s) => (
          <span key={s} className="flex items-center gap-1.5 capitalize">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_DOT_COLOR[s] }} />
            {s.replace('-', ' ')}
          </span>
        ))}
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-text-dim py-1">{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-px" style={{ background: 'var(--color-border)' }}>
        {/* Empty cells before month start */}
        {Array.from({ length: startDow }).map((_, i) => (
          <div key={`e${i}`} className="bg-card2 min-h-[56px]" />
        ))}
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const entries = dueDateMap[key] || [];
          const isToday = isSameDay(day, TODAY);
          return (
            <div
              key={key}
              className="bg-card min-h-[56px] p-1 flex flex-col"
              style={isToday ? { background: 'rgba(13,148,136,0.06)', outline: '1.5px solid #b07d1a', outlineOffset: '-1px' } : {}}
            >
              <span className={`text-[11px] font-semibold mb-1 ${isToday ? 'text-teal' : 'text-text-dim'}`}>{format(day, 'd')}</span>
              {entries.slice(0, 3).map((e, i) => (
                <span key={i} className="flex items-center gap-1 text-[9px] font-medium truncate leading-tight mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: STATUS_DOT_COLOR[e.status] || '#64748b' }} />
                  <span className="text-text-dim truncate">{e.name}</span>
                </span>
              ))}
              {entries.length > 3 && <span className="text-[9px] text-text-dim">+{entries.length - 3} more</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function CalibrationPM() {
  const [search, setSearch] = useState('');
  const [labFilter, setLabFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [page, setPage] = useState(1);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selected, setSelected] = useState<Equipment | null>(null);
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [form, setForm] = useState({
    equipment: '',
    serviceType: 'calibration',
    scheduledDate: '',
    technician: '',
    notes: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['calibration-pm', { search, lab: labFilter, status: statusFilter, type: typeFilter, page }],
    queryFn: () =>
      api.get('/calibration-pm', { params: { search, lab: labFilter, status: statusFilter, type: typeFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_EQUIPMENT, meta: { total: MOCK_EQUIPMENT.length } })),
  });

  const rows: Equipment[] = data?.data ?? MOCK_EQUIPMENT;
  const total: number = data?.meta?.total ?? MOCK_EQUIPMENT.length;

  const filtered = rows.filter((e) => {
    const matchSearch = !search || e.equipmentName.toLowerCase().includes(search.toLowerCase()) || e.equipmentCode.toLowerCase().includes(search.toLowerCase());
    const matchLab = !labFilter || e.lab === labFilter;
    const matchStatus = !statusFilter || e.status === statusFilter;
    const matchType = !typeFilter || e.type === typeFilter;
    return matchSearch && matchLab && matchStatus && matchType;
  });

  const scheduleMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/calibration-pm/schedule', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration-pm'] });
      setShowSchedule(false);
      setForm({ equipment: '', serviceType: 'calibration', scheduledDate: '', technician: '', notes: '' });
    },
  });

  const exportColumns = [
    { key: 'equipmentName', label: 'Equipment Name' },
    { key: 'equipmentCode', label: 'Code' },
    { key: 'lab', label: 'Lab' },
    { key: 'type', label: 'Type' },
    { key: 'lastCalibration', label: 'Last Calibration' },
    { key: 'nextCalibrationDue', label: 'Next Calibration Due' },
    { key: 'lastPM', label: 'Last PM' },
    { key: 'nextPMDue', label: 'Next PM Due' },
    { key: 'status', label: 'Status' },
    { key: 'responsibleTechnician', label: 'Technician' },
  ];
  const { exportCSV, exportPDF } = useTableExport(filtered as unknown as Record<string, unknown>[], exportColumns, 'calibration-pm-schedule');

  const { saved: savedFilters, saveFilter, loadFilter, deleteFilter } = useSavedFilters('calibration-pm');

  const thisMonthStr = format(TODAY, 'yyyy-MM');
  const stats = {
    total: filtered.length,
    calibrationDue: filtered.filter((e) => e.status === 'due-soon').length,
    pmDue: filtered.filter((e) => e.nextPMDue && e.nextPMDue.startsWith(thisMonthStr)).length,
    overdue: filtered.filter((e) => e.status === 'overdue').length,
    completedThisMonth: filtered.filter((e) => {
      if (!e.lastCalibration && !e.lastPM) return false;
      return (e.lastCalibration?.startsWith(thisMonthStr)) || (e.lastPM?.startsWith(thisMonthStr));
    }).length,
  };

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'equipmentName', label: 'Equipment',
      render: (row) => (
        <div>
          <p className="font-medium text-sm text-text">{String(row.equipmentName)}</p>
          <p className="text-xs font-mono text-text-dim">{String(row.equipmentCode)}</p>
        </div>
      ),
    },
    {
      key: 'lab', label: 'Lab', width: 'w-32',
      render: (row) => <span className="text-xs text-text-muted">{String(row.lab)}</span>,
    },
    {
      key: 'type', label: 'Type', width: 'w-28',
      render: (row) => (
        <span className="text-xs font-medium capitalize px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{String(row.type)}</span>
      ),
    },
    {
      key: 'nextCalibrationDue', label: 'Calibration Due', width: 'w-36',
      render: (row) => {
        const d = row.nextCalibrationDue ? String(row.nextCalibrationDue) : null;
        if (!d) return <span className="text-xs text-text-dim">—</span>;
        const due = new Date(d);
        const isPast = isBefore(due, TODAY);
        const isSoon = !isPast && differenceInDaysSimple(due, TODAY) <= 30;
        return (
          <div>
            <p className="text-xs font-mono" style={{ color: isPast ? '#DC2626' : isSoon ? '#D97706' : undefined }}>{format(due, 'd MMM yyyy')}</p>
            {isPast && <p className="text-[10px] font-semibold text-red-600">Overdue</p>}
            {isSoon && !isPast && <p className="text-[10px] font-semibold text-orange-600">Due soon</p>}
          </div>
        );
      },
    },
    {
      key: 'nextPMDue', label: 'PM Due', width: 'w-32',
      render: (row) => {
        const d = row.nextPMDue ? String(row.nextPMDue) : null;
        if (!d) return <span className="text-xs text-text-dim">—</span>;
        return <span className="text-xs font-mono text-text-muted">{format(new Date(d), 'd MMM yyyy')}</span>;
      },
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'responsibleTechnician', label: 'Technician',
      render: (row) => <span className="text-xs text-text-muted">{String(row.responsibleTechnician)}</span>,
    },
  ];

  const handleApplyFilter = (id: string) => {
    const f = loadFilter(id);
    if (!f) return;
    if (f.status !== undefined) setStatusFilter(f.status);
    if (f.lab !== undefined) setLabFilter(f.lab);
    if (f.type !== undefined) setTypeFilter(f.type);
    if (f.search !== undefined) setSearch(f.search);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Calibration & PM Schedules"
        subtitle="Preventive maintenance · Calibration due dates · Service history"
        actions={
          <>
            {/* View toggle */}
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-teal text-white' : 'bg-card text-text-muted hover:bg-bg'}`}
              >
                <List size={13} /> List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'calendar' ? 'bg-teal text-white' : 'bg-card text-text-muted hover:bg-bg'}`}
              >
                <CalendarDays size={13} /> Calendar
              </button>
            </div>
            <button onClick={exportPDF} className="btn-ghost"><FileText size={13} /> PDF</button>
            <button onClick={exportCSV} className="btn-ghost"><Download size={13} /> CSV</button>
            <button onClick={() => setShowSchedule(true)} className="btn-primary"><Plus size={13} /> Schedule Service</button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Equipment" value={stats.total} icon={Wrench} color="#b07d1a" />
        <StatCard label="Calibration Due" value={stats.calibrationDue} icon={AlertTriangle} color="#D97706" />
        <StatCard label="PM Due This Month" value={stats.pmDue} icon={Clock} color="#2563EB" />
        <StatCard label="Overdue" value={stats.overdue} icon={AlertTriangle} color="#DC2626" />
        <StatCard label="Completed This Month" value={stats.completedThisMonth} icon={CheckCircle2} color="#16A34A" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by equipment name or code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>

        <select value={labFilter} onChange={(e) => { setLabFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Labs</option>
          {LABS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>

        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
        </select>

        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
        </select>

        {/* Saved Filters */}
        <div className="relative">
          <button onClick={() => setShowSaveFilter(!showSaveFilter)} className="btn-ghost">
            <Bookmark size={13} /> Saved Filters
            {savedFilters.length > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-teal text-white text-[9px] flex items-center justify-center">{savedFilters.length}</span>
            )}
          </button>
          {showSaveFilter && (
            <div className="absolute right-0 top-10 z-30 card p-3 w-60 space-y-2 shadow-xl">
              <p className="text-xs font-semibold text-text-muted">Saved Filters</p>
              {savedFilters.length === 0 && <p className="text-xs text-text-dim">No saved filters yet.</p>}
              {savedFilters.map((f) => (
                <div key={f.id} className="flex items-center justify-between group">
                  <button onClick={() => { handleApplyFilter(f.id); setShowSaveFilter(false); }} className="text-xs text-text hover:text-teal">{f.name}</button>
                  <button onClick={() => deleteFilter(f.id)} className="text-[10px] text-red-500 opacity-0 group-hover:opacity-100">remove</button>
                </div>
              ))}
              <div className="border-t border-border pt-2 flex gap-1">
                <input className="input text-xs flex-1 py-1" placeholder="Filter name..." value={saveFilterName} onChange={(e) => setSaveFilterName(e.target.value)} />
                <button
                  onClick={() => {
                    if (saveFilterName) {
                      saveFilter(saveFilterName, { status: statusFilter, lab: labFilter, type: typeFilter, search });
                      setSaveFilterName('');
                    }
                  }}
                  className="btn-primary text-xs py-1 px-2"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* List or Calendar */}
      {viewMode === 'list' ? (
        <DataTable
          columns={columns}
          data={filtered as unknown as Record<string, unknown>[]}
          loading={isLoading}
          onRowClick={(row) => setSelected(row as unknown as Equipment)}
          total={total}
          page={page}
          onPageChange={setPage}
        />
      ) : (
        <CalendarView equipment={filtered} />
      )}

      {/* Schedule Service Modal */}
      <Modal
        isOpen={showSchedule}
        onClose={() => setShowSchedule(false)}
        title="Schedule Service"
        subtitle="Calibration, PM, repair or inspection"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowSchedule(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => scheduleMutation.mutate(form)} disabled={scheduleMutation.isPending} className="btn-primary">
              {scheduleMutation.isPending ? 'Scheduling...' : 'Schedule'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Equipment *</label>
            <select className="select" value={form.equipment} onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))}>
              <option value="">Select equipment...</option>
              {(rows.length ? rows : MOCK_EQUIPMENT).map((eq) => (
                <option key={eq.id} value={eq.id}>{eq.equipmentName} ({eq.equipmentCode})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Service Type *</label>
            <select className="select" value={form.serviceType} onChange={(e) => setForm((f) => ({ ...f, serviceType: e.target.value }))}>
              {SERVICE_TYPES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Scheduled Date *</label>
            <input type="date" className="input" value={form.scheduledDate} onChange={(e) => setForm((f) => ({ ...f, scheduledDate: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Responsible Technician *</label>
            <input className="input" value={form.technician} onChange={(e) => setForm((f) => ({ ...f, technician: e.target.value }))} placeholder="Technician name or vendor..." />
          </div>
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Special instructions, scope of work, reference SOP..." />
          </div>
        </div>
      </Modal>

      {/* Equipment Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.equipmentCode || ''}
        subtitle={selected?.equipmentName}
        width={540}
      >
        {selected && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Lab', value: selected.lab },
                { label: 'Type', value: selected.type },
                { label: 'Responsible', value: selected.responsibleTechnician },
                { label: 'Status', node: <PillBadge value={selected.status} showDot /> },
                { label: 'Last Calibration', value: selected.lastCalibration ? format(new Date(selected.lastCalibration), 'd MMM yyyy') : '—' },
                { label: 'Next Calibration', value: selected.nextCalibrationDue ? format(new Date(selected.nextCalibrationDue), 'd MMM yyyy') : '—' },
                { label: 'Last PM', value: selected.lastPM ? format(new Date(selected.lastPM), 'd MMM yyyy') : '—' },
                { label: 'Next PM Due', value: selected.nextPMDue ? format(new Date(selected.nextPMDue), 'd MMM yyyy') : '—' },
              ].map(({ label, value, node }) => (
                <div key={label} className="p-3 rounded-lg bg-bg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <div className="text-xs font-medium text-text">{node || value}</div>
                </div>
              ))}
            </div>

            {/* Calibration Certificates */}
            {(selected.certificates || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                  <FileText size={12} className="text-teal" /> Calibration Certificates
                </p>
                <div className="space-y-2">
                  {selected.certificates!.map((cert) => (
                    <div key={cert.id} className="p-3 rounded-lg border border-border flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-text">{cert.name}</p>
                        <p className="text-[11px] text-text-dim">Issued: {format(new Date(cert.issuedDate), 'd MMM yyyy')} · Expires: {format(new Date(cert.expiryDate), 'd MMM yyyy')}</p>
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">On File</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service History Timeline */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-3 flex items-center gap-1.5">
                <Wrench size={12} className="text-teal" /> Service History
              </p>
              {(selected.serviceHistory || []).length === 0 ? (
                <p className="text-xs text-text-dim">No service history recorded.</p>
              ) : (
                <div className="space-y-0">
                  {selected.serviceHistory!.map((sh, idx) => (
                    <div key={sh.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                          style={{ background: sh.serviceType === 'calibration' ? '#b07d1a' : sh.serviceType === 'repair' ? '#DC2626' : sh.serviceType === 'inspection' ? '#2563EB' : '#D97706' }}>
                          {sh.serviceType[0].toUpperCase()}
                        </div>
                        {idx < (selected.serviceHistory?.length || 0) - 1 && (
                          <div className="w-px flex-1 my-1" style={{ background: 'var(--color-border)' }} />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="text-xs font-semibold text-text capitalize">{sh.serviceType}</p>
                        <p className="text-[11px] text-text-dim mt-0.5">
                          {format(new Date(sh.date), 'd MMM yyyy')} · {sh.technician}
                        </p>
                        {sh.notes && <p className="text-[11px] text-text-muted mt-1">{sh.notes}</p>}
                        {sh.certificateRef && (
                          <p className="text-[11px] mt-1">
                            <span className="font-medium text-teal">Cert Ref:</span> <span className="text-text-dim font-mono">{sh.certificateRef}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SideDrawer>
    </div>
  );
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function differenceInDaysSimple(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}
